import { useState } from 'react';

import PropTypes from 'prop-types';
import { HiPlus } from 'react-icons/hi2';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

/**
 * Build a merge token from a variable name + optional fallback, in the right brace style.
 * @param {string} name - variable name, e.g. 'firstName' or 'vorname'
 * @param {string} fallback - replacement value when the field is empty ('' = none)
 * @param {'single'|'double'} braces
 * @returns {string} e.g. "{firstName}", "{firstName|fallback=Team}", "{{anrede|fallback=…}}"
 */
function buildToken(name, fallback, braces) {
	const inner = fallback.trim() ? `${name}|fallback=${fallback.trim()}` : name;
	return braces === 'double' ? `{{${inner}}}` : `{${inner}}`;
}

/**
 * One variable pill. Clicking it opens a small popover that lets the user (optionally) set a
 * replacement value for when the recipient field is empty (e.g. a company recipient with no
 * contact person). Confirming inserts the merge token via onInsert.
 */
const VariablePill = ({ variable, braces, align, onInsert }) => {
	const [open, setOpen] = useState(false);
	const [fallback, setFallback] = useState('');

	const confirm = () => {
		onInsert(buildToken(variable.name, fallback, braces));
		setFallback('');
		setOpen(false);
	};

	const trigger = (
		<button
			type="button"
			onClick={() => setOpen((v) => !v)}
			className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-gray-50 border border-gray-200 rounded-md text-text hover:bg-primary-100 hover:border-primary-200 hover:text-primary-700 transition-colors"
			title={`${variable.label} einfügen – ${buildToken(variable.name, '', braces)}`}
		>
			<HiPlus className="w-3 h-3" />
			{variable.label}
		</button>
	);

	return (
		<Popover
			open={open}
			onClose={() => setOpen(false)}
			trigger={trigger}
			size="s"
			align={align === 'right' ? 'end' : 'start'}
		>
			<Panel.Body>
				<p className="text-xs font-semibold text-text mb-1">{variable.label} einfügen</p>
				<label className="block text-[11px] text-bgBase-800 mb-1">Ersatzwert, falls leer (optional)</label>
				<TextInput
					value={fallback}
					onChange={(e) => setFallback(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							confirm();
						}
					}}
					placeholder={variable.fallbackPlaceholder || 'z.B. Team'}
					autoFocus
				/>
				<p className="text-[10px] text-bgBase-800 mt-1">
					Wird angezeigt, wenn der Empfänger kein {variable.label.toLowerCase()} hat (z.B. Firmen ohne
					Ansprechpartner).
				</p>
				<div className="flex justify-end mt-2">
					<Button onClick={confirm} size="sm">
						Einfügen
					</Button>
				</div>
			</Panel.Body>
		</Popover>
	);
};

VariablePill.propTypes = {
	variable: PropTypes.shape({
		name: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		fallbackPlaceholder: PropTypes.string,
	}).isRequired,
	braces: PropTypes.oneOf(['single', 'double']).isRequired,
	align: PropTypes.oneOf(['left', 'right']),
	onInsert: PropTypes.func.isRequired,
};

/**
 * A row of variable pills with per-variable fallback popovers. Inserts merge tokens like
 * {firstName} / {firstName|fallback=Team} (single braces) or the {{…}} variant. An optional
 * `trailing` slot renders extra controls (e.g. a greeting configurator) in the same pill row.
 * `align='right'` opens the popovers leftwards (use in right-side panels to avoid overflow).
 */
const VariableInserter = ({ variables, braces = 'single', align = 'left', onInsert, trailing }) => (
	<div className="flex flex-wrap items-start gap-1.5">
		{variables.map((v) => (
			<VariablePill key={v.name} variable={v} braces={braces} align={align} onInsert={onInsert} />
		))}
		{trailing}
	</div>
);

VariableInserter.propTypes = {
	variables: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			fallbackPlaceholder: PropTypes.string,
		}),
	).isRequired,
	braces: PropTypes.oneOf(['single', 'double']),
	align: PropTypes.oneOf(['left', 'right']),
	onInsert: PropTypes.func.isRequired,
	trailing: PropTypes.node,
};

export default VariableInserter;
